/**
 * Pure conversation-action kernels for the sidebar — extracted verbatim
 * (behavior-preserving) from the inline `handleConversationClick`,
 * `handleCreateConversation`, and `handleImportConversations` bodies in
 * layout/Sidebar.tsx. These isolate the deterministic decision/value math so the
 * branchy parts (remembered-module resolution, the explicit camelCase import
 * field pick, the new-conversation object shape) can be characterization-tested
 * without React, navigation, or the async createConversation side effects.
 *
 * The stateful pieces (rate-limit guard, navigation, sessionStorage read,
 * createConversation calls) live in `useConversationActions`; this file holds
 * only the pure transforms.
 */

import type { Conversation } from '@/types/conversations';
import { parseConversationModules } from '@/types/conversations';
import { isSummaryModuleType } from '@/types/modules';

/**
 * Resolve which module to open when a conversation row is clicked. Verbatim port
 * of the inline logic in `handleConversationClick`:
 *
 *   1. If a remembered module id is supplied (from sessionStorage), it wins —
 *      returned as-is WITHOUT validating it against the conversation's modules
 *      (matches the original, which only computed a fallback when there was no
 *      remembered module).
 *   2. Otherwise normalize modules the same way ConversationContext does
 *      (`conversation.modules ?? parseConversationModules(conversation.meta?.modules) ?? []`),
 *      drop archived + summary-type modules, sort by ascending `position`, and
 *      pick the first id.
 *   3. If nothing resolves, return `null` (the caller navigates without a module).
 */
export function resolveClickTargetModule(
  rememberedModule: string | null,
  conversation: Pick<Conversation, 'modules' | 'meta'>,
): string | null {
  let targetModule = rememberedModule;

  const modules = conversation.modules ?? parseConversationModules(conversation.meta?.modules) ?? [];

  if (!targetModule && modules.length > 0) {
    const sortedModules = modules
      .filter((mod) => !mod.archived && !isSummaryModuleType(mod.type))
      .sort((a, b) => a.position - b.position);
    targetModule = sortedModules[0]?.id ?? null;
  }

  return targetModule ?? null;
}

/**
 * Build the new Conversation object for `handleCreateConversation`. The id is
 * derived from the supplied `now` timestamp (the only field that varies between
 * runs); everything else is the verbatim object literal from the inline handler,
 * including the carried-over `existingModules`.
 */
export function buildNewConversation(args: {
  conversationName: string;
  existingModules: Conversation['modules'];
  now: number;
}): Conversation {
  const { conversationName, existingModules, now } = args;
  return {
    id: `conversation-${now}`,
    name: conversationName,
    icon: '📁',
    color: 'var(--color-primary)',
    visibility: 'private',
    modules: existingModules ?? [],
  };
}

/**
 * Build a Conversation for import. Verbatim port of the explicit camelCase field
 * pick in `handleImportConversations` — the comment there is load-bearing: the
 * import source is unvalidated `JSON.parse`, so a pre-#2448 export can carry
 * snake_case timestamp fields the type no longer admits. Spreading would
 * propagate them; instead we pick the known camelCase fields explicitly. The id
 * is freshly minted from the supplied `now`/`rand` so re-imports never collide.
 */
export function buildImportedConversation(
  conversation: Conversation,
  now: number,
  rand: string,
): Conversation {
  return {
    id: `conversation-${now}-${rand}`,
    name: conversation.name,
    icon: conversation.icon,
    color: conversation.color,
    visibility: conversation.visibility,
    archivedAt: conversation.archivedAt,
    createdAt: conversation.createdAt,
    updatedAt: conversation.updatedAt,
    version: conversation.version,
    position: conversation.position,
    meta: conversation.meta,
    modules: conversation.modules,
    _etag: conversation._etag,
  };
}
